import { Component } from '@theme/component';

/**
 * Desktop secondary navigation bar (N2) + mega panel (N3/N4).
 *
 * Two coordinated hover layers:
 *  - Top-row N1 links (rendered in a sibling header block) reveal that N1's N2
 *    set; leaving the header reverts to the default set (active section, or the
 *    first N1).
 *  - Hovering / focusing an N2 link that has children opens its mega panel and
 *    lights its gold indicator; the panel stays open while hovered.
 *
 * The N1 items live outside this element, so listeners are wired manually and
 * torn down via an AbortController. Short close delays bridge the pointer gap
 * between rows and between the bar and the panel.
 *
 * @extends {Component}
 */
class HeaderSecondary extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {Map<string, HTMLElement>} */
  #sets = new Map();

  /** @type {HTMLElement | null} */
  #defaultSet = null;

  /** @type {number | undefined} */
  #revertTimer;

  /** @type {number | undefined} */
  #panelTimer;

  /** @type {Element | null} */
  #bannerSource = null;

  /** @type {MutationObserver | null} */
  #hydrationObserver = null;

  /** @type {HTMLElement | null} */
  #topRow = null;

  /** @type {number} */
  #resizeRaf = 0;

  connectedCallback() {
    super.connectedCallback();

    const sets = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.header-secondary__set')]);
    if (sets.length === 0) return;

    for (const set of sets) {
      const handle = set.dataset.n1Handle;
      if (handle) this.#sets.set(handle, set);
    }
    // May be null on pages with no active N1 (e.g. home) — then the bar hides on leave.
    this.#defaultSet = sets.find((set) => set.dataset.default === 'true') ?? null;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const root = this.closest('#header-component') ?? document;

    // Banners are rendered at section scope (section.blocks isn't readable from inside
    // the header-menu block) into a hidden source; clone each into its matching N2
    // panel. The header re-renders via the Section Rendering API, wiping DOM we inject,
    // so we (a) clone rather than move (the source stays intact) and (b) re-run on
    // panel mutations to survive hydration.
    this.#bannerSource = root.querySelector('.header-mega-banner-source');
    if (this.#bannerSource) {
      this.#adoptBanners();
      this.#hydrationObserver = new MutationObserver(() => this.#adoptBanners());
      this.#hydrationObserver.observe(this, { childList: true, subtree: true });
    }

    // Layer 1: top-row N1 → reveal its N2 set. Delegated on the top row (not bound to
    // each <li>) so it survives the header re-rendering via the Section Rendering API,
    // which replaces the N1 nodes.
    const topRow = /** @type {HTMLElement | null} */ (root.querySelector('.header-menu'));
    this.#topRow = topRow;
    topRow?.addEventListener('pointerover', this.#onN1, { signal });
    topRow?.addEventListener('focusin', this.#onN1, { signal });
    topRow?.addEventListener('pointerleave', this.#scheduleRevert, { signal });

    // Layer 2: N2 links → open/close their mega panel.
    for (const link of this.querySelectorAll('.header-secondary__link')) {
      link.addEventListener('pointerenter', () => this.#onN2Enter(/** @type {HTMLElement} */ (link)), { signal });
      link.addEventListener('focusin', () => this.#onN2Enter(/** @type {HTMLElement} */ (link)), { signal });
    }
    for (const panel of this.querySelectorAll('.header-secondary__panel')) {
      panel.addEventListener('pointerenter', this.#cancelPanelClose, { signal });
      panel.addEventListener('pointerleave', this.#scheduleClosePanel, { signal });
      // Keep the panel open while keyboard focus is inside it.
      panel.addEventListener('focusin', this.#cancelPanelClose, { signal });
    }

    // Keep the current state while hovering/focusing the bar; revert/close on leave.
    this.addEventListener('pointerenter', this.#cancelRevert, { signal });
    this.addEventListener('focusin', this.#cancelRevert, { signal });
    this.addEventListener('pointerleave', this.#onLeave, { signal });
    this.addEventListener('focusout', this.#onLeave, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });

    // On category pages the default set's bar/panel is shown persistently and, being
    // absolutely positioned, would overlay the first page content (breadcrumbs).
    // Publish its live height so CSS can reserve the space (see #reserveSpace).
    this.#reserveSpace();
    window.addEventListener('resize', this.#onResize, { signal });
    document.fonts?.ready?.then(() => this.#reserveSpace());
  }

  /**
   * Delegated top-row N1 hover/focus → reveal that N1's N2 set. Skips when the set is
   * already visible so repeated `pointerover` bubbling doesn't re-close an open panel.
   * @param {Event} event
   */
  #onN1 = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const item = target.closest('[data-n1-handle]');
    const handle = /** @type {HTMLElement | null} */ (item)?.dataset.n1Handle;
    if (!handle) return;
    const current = /** @type {HTMLElement | null} */ (
      this.querySelector('.header-secondary__set--visible')
    )?.dataset.n1Handle;
    if (handle === current) return;
    this.#showSet(handle);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const active = this.querySelector('.header-secondary__link--active');
    this.#closePanel();
    if (active instanceof HTMLElement) active.focus();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#hydrationObserver?.disconnect();
    this.#hydrationObserver = null;
    clearTimeout(this.#revertTimer);
    clearTimeout(this.#panelTimer);
    cancelAnimationFrame(this.#resizeRaf);
  }

  /**
   * Clone the hidden section-scope banner cards into the mega panel of the N2 whose
   * handle matches each banner's `data-n2-handle`. Idempotent and non-destructive (the
   * source is kept) so it can re-run after the header re-renders via the Section
   * Rendering API. The observer is paused during mutation to avoid a feedback loop.
   */
  #adoptBanners = () => {
    const source = this.#bannerSource;
    if (!source) return;

    /** @type {Map<string, Element[]>} */
    const byHandle = new Map();
    for (const item of source.querySelectorAll('.header-mega-banner-source__item')) {
      const handle = /** @type {HTMLElement} */ (item).dataset.n2Handle;
      const card = item.querySelector('.mega-panel__banner');
      if (!handle || !card) continue;
      if (!byHandle.has(handle)) byHandle.set(handle, []);
      byHandle.get(handle)?.push(card);
    }
    if (byHandle.size === 0) return;

    this.#hydrationObserver?.disconnect();

    // QA #2: a composite "n1__n2" banner is more specific than a bare "n2" and must
    // win for that N1's panel. Process composite keys first and record which panels
    // they claim, so the bare-handle pass skips those panels rather than racing to
    // overwrite them. This makes desktop deterministic and consistent with mobile
    // (drawer-mega-banners.js already prefers the composite key per slot).
    const entries = [...byHandle.entries()].sort(
      ([a], [b]) => (b.includes('__') ? 1 : 0) - (a.includes('__') ? 1 : 0)
    );
    /** @type {Set<Element>} */
    const claimedByComposite = new Set();

    for (const [handle, cards] of entries) {
      const parts = handle.split('__');
      const isComposite = parts.length === 2;
      let panelSelector;
      let linkSelector;
      if (isComposite) {
        const setSel = `.header-secondary__set[data-n1-handle="${CSS.escape(parts[0])}"]`;
        panelSelector = `${setSel} .header-secondary__panel[data-n2-handle="${CSS.escape(handle)}"] .mega-panel`;
        linkSelector = `${setSel} .header-secondary__link[data-n2-handle="${CSS.escape(parts[1])}"]`;
      } else {
        panelSelector = `.header-secondary__panel[data-n2="${CSS.escape(handle)}"] .mega-panel`;
        linkSelector = `.header-secondary__link[data-n2-handle="${CSS.escape(handle)}"]`;
      }
      // A handle may appear in more than one N1's set — populate every matching panel.
      const panels = this.querySelectorAll(panelSelector);
      for (const panel of panels) {
        // A composite banner already owns this panel → a bare-handle banner must not
        // override it (specificity wins, regardless of block order).
        if (!isComposite && claimedByComposite.has(panel)) continue;
        if (isComposite) claimedByComposite.add(panel);
        let banners = panel.querySelector('.mega-panel__banners');
        if (!banners) {
          banners = document.createElement('div');
          banners.className = 'mega-panel__banners';
          panel.appendChild(banners);
        }
        // Idempotent: only rewrite when the panel isn't already populated correctly.
        if (banners.childElementCount === cards.length) continue;
        banners.replaceChildren(...cards.map((card) => card.cloneNode(true)));
      }

      // Enable the N2 link(s) with this banner as a hover trigger even when the
      // N2 has no children (its only content is the adopted banner). Server-side
      // we can't know a banner block exists, so wire the trigger here.
      for (const link of this.querySelectorAll(linkSelector)) {
        link.classList.add('header-secondary__link--has-panel');
        link.setAttribute('aria-haspopup', 'true');
        if (!link.hasAttribute('aria-expanded')) link.setAttribute('aria-expanded', 'false');
      }
    }

    if (this.isConnected) {
      this.#hydrationObserver?.observe(this, { childList: true, subtree: true });
    }

    // Fresh clones carry no image URLs; give them to panels already showing.
    this.#loadShownBanners();

    // Adopted banners can grow a persistent single-N2 panel — re-measure the reserve.
    this.#reserveSpace();
  };

  /**
   * Adopted banner images carry their URLs in data-* attributes (see
   * blocks/mega-menu-banner.liquid). Closed panels are only visibility:hidden, so
   * loading="lazy" alone would still download every banner at page load: promote the
   * URLs just for the panels that are showing — the open one, or the always-visible
   * panel of a single-N2 set. Called whenever that changes.
   */
  #loadShownBanners() {
    const shown = this.querySelectorAll(
      '.header-secondary__panel--open, .header-secondary__set--single.header-secondary__set--visible .header-secondary__panel'
    );
    for (const panel of shown) loadBannerImages(panel);
  }

  /**
   * On a category page one N1 set is shown by default (server-rendered `data-default`);
   * that set's N2 bar (or, for single-N2 N1s, its mega panel) is absolutely positioned
   * and would sit over the first page section. Publish its rendered height as
   * `--header-secondary-reserve` so CSS can push the content below it. 0 when no default
   * set exists (home, child collections, product pages…), leaving every other page
   * untouched. Measuring the default set only (not the hovered one) keeps the reserve
   * stable, so opening another N1's panel on hover never reflows the page.
   *
   * The initial value is set synchronously in theme.liquid (before <main> paints, so
   * there is no load-time layout shift); this keeps it in sync on resize / font load /
   * banner adoption. Writes the same body-level var so the two never conflict.
   */
  #reserveSpace = () => {
    const set = this.#defaultSet;
    let height = 0;
    if (set) {
      const el =
        set.querySelector('.header-secondary__bar') ?? set.querySelector('.header-secondary__panel');
      if (el instanceof HTMLElement) height = el.offsetHeight;
    }
    document.body.style.setProperty('--header-secondary-reserve', `${height}px`);
  };

  #onResize = () => {
    if (this.#resizeRaf) return;
    this.#resizeRaf = requestAnimationFrame(() => {
      this.#resizeRaf = 0;
      this.#reserveSpace();
    });
  };

  /**
   * Reveal the N2 set matching the given N1 handle (hide the rest); switching
   * N1 always closes any open panel.
   * @param {string} handle
   */
  #showSet(handle) {
    this.#cancelRevert();
    this.#closePanel();
    const target = this.#sets.get(handle) ?? null;
    for (const set of this.#sets.values()) {
      set.classList.toggle('header-secondary__set--visible', set === target);
    }
    this.#markN1(handle);
    this.#loadShownBanners();
  }

  /**
   * Keep the top-row N1 whose set is showing highlighted ("browsing") while the
   * pointer moves down into its N2 bar / mega panel — otherwise the N1's own
   * `:hover` is lost and the user can't tell which section they're in. Pass null
   * to clear (on revert). Highlighted items (REBAJAS) keep their own color via CSS.
   * @param {string | null} handle
   */
  #markN1(handle) {
    if (!this.#topRow) return;
    for (const link of this.#topRow.querySelectorAll('.menu-list__link')) {
      const item = link.closest('[data-n1-handle]');
      const isBrowsing = handle != null && item instanceof HTMLElement && item.dataset.n1Handle === handle;
      link.classList.toggle('menu-list__link--browsing', isBrowsing);
    }
  }

  /** @param {HTMLElement} link */
  #onN2Enter(link) {
    this.#cancelPanelClose();
    const handle = link.dataset.n2Handle;
    const hasPanel = link.classList.contains('header-secondary__link--has-panel');
    if (!hasPanel || !handle) {
      this.#closePanel();
      return;
    }
    this.#openPanel(link, handle);
  }

  /**
   * @param {HTMLElement} link
   * @param {string} handle
   */
  #openPanel(link, handle) {
    const set = link.closest('.header-secondary__set');
    if (!set) return;
    for (const l of set.querySelectorAll('.header-secondary__link')) {
      const isActive = l === link;
      l.classList.toggle('header-secondary__link--active', isActive);
      if (l.hasAttribute('aria-haspopup')) l.setAttribute('aria-expanded', String(isActive));
    }
    for (const panel of set.querySelectorAll('.header-secondary__panel')) {
      // The N2 link's handle is plain ("paseo"); the panel's `data-n2-handle` may be a
      // composite "n1__n2" (QA #2, for banner routing), so match on the plain `data-n2`
      // first and fall back to the (legacy) plain `data-n2-handle`.
      const p = /** @type {HTMLElement} */ (panel);
      const isOpen = p.dataset.n2 === handle || p.dataset.n2Handle === handle;
      panel.classList.toggle('header-secondary__panel--open', isOpen);
    }
    this.#loadShownBanners();
  }

  #closePanel = () => {
    for (const link of this.querySelectorAll('.header-secondary__link--active')) {
      link.classList.remove('header-secondary__link--active');
      if (link.hasAttribute('aria-haspopup')) link.setAttribute('aria-expanded', 'false');
    }
    for (const panel of this.querySelectorAll('.header-secondary__panel--open')) {
      panel.classList.remove('header-secondary__panel--open');
    }
  };

  /** @param {Event} [event] */
  #onLeave = (event) => {
    // Ignore focus/pointer moves that stay inside the component (e.g. N2 link → panel).
    const to = /** @type {Node | null} */ (
      event && 'relatedTarget' in event ? /** @type {any} */ (event).relatedTarget : null
    );
    if (to && this.contains(to)) return;
    this.#scheduleRevert();
    this.#scheduleClosePanel();
  };

  #cancelPanelClose = () => clearTimeout(this.#panelTimer);

  #scheduleClosePanel = () => {
    clearTimeout(this.#panelTimer);
    this.#panelTimer = setTimeout(this.#closePanel, 120);
  };

  #cancelRevert = () => clearTimeout(this.#revertTimer);

  #scheduleRevert = () => {
    clearTimeout(this.#revertTimer);
    this.#revertTimer = setTimeout(() => {
      for (const set of this.#sets.values()) {
        set.classList.toggle('header-secondary__set--visible', set === this.#defaultSet);
      }
      // Pointer left the header — clear the transient browsing highlight (the
      // current-page N1 keeps its own --active gold from the server).
      this.#markN1(null);
      this.#loadShownBanners();
    }, 150);
  };
}

/**
 * Promote deferred banner image URLs (data-sizes / data-srcset / data-src) to the real
 * attributes. They are deferred because cloning an <img> that already has src/srcset
 * makes Chrome fetch it immediately (see blocks/mega-menu-banner.liquid).
 * @param {ParentNode} root
 */
function loadBannerImages(root) {
  for (const img of root.querySelectorAll('img[data-src]')) {
    const { sizes, srcset, src } = /** @type {HTMLImageElement} */ (img).dataset;
    if (sizes) img.setAttribute('sizes', sizes);
    if (srcset) img.setAttribute('srcset', srcset);
    if (src) img.setAttribute('src', src);
    img.removeAttribute('data-sizes');
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
  }
}

if (!customElements.get('header-secondary')) {
  customElements.define('header-secondary', HeaderSecondary);
}
